/**
 * Billing page — wallet balance + subscription management + invoice history.
 * Invoice table is clean, sortable-feeling, with per-row "Download PDF" stub.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import WalletBalanceCard from '../components/WalletBalanceCard';
import StripeConnectCard from '../components/billing/StripeConnectCard';
import { CreditCard, Wallet, Download, Check, Loader2, Crown, ChevronRight, Waves, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [smartPresets, setSmartPresets] = useState([]);
  const [chosenPkg, setChosenPkg] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [plansR, walletR, txR, presetsR] = await Promise.all([
        axios.get(`${API}/api/payments/plans`),
        axios.get(`${API}/api/payments/wallet`, { withCredentials: true }),
        axios.get(`${API}/api/payments/transactions`, { withCredentials: true }),
        axios.get(`${API}/api/payments/smart-presets`, { withCredentials: true }),
      ]);
      setPlans(plansR.data);
      setWallet(walletR.data);
      setTxs(txR.data.transactions || []);
      setSmartPresets(presetsR.data.presets || []);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('billing load failed:', e?.response?.status || e?.message);
      setError('Could not load billing data.');
    }
  };

  useEffect(() => { load(); }, []);

  const topup = async (overrideAmount) => {
    const payload = { origin_url: window.location.origin + '/billing' };
    if (typeof overrideAmount === 'number' && overrideAmount > 0) {
      payload.custom_amount = overrideAmount;
    } else if (chosenPkg) {
      payload.package_id = chosenPkg;
    } else if (customAmount) {
      payload.custom_amount = parseFloat(customAmount);
    } else {
      setError('Pick a package or enter a custom amount.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const r = await axios.post(`${API}/api/payments/wallet/topup`, payload, { withCredentials: true });
      window.location.href = r.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start top-up.');
      setBusy(false);
    }
  };

  const subscribe = async (tierId) => {
    setBusy(true); setError(null);
    try {
      const r = await axios.post(`${API}/api/payments/subscribe`,
        { tier: tierId, origin_url: window.location.origin + '/billing' },
        { withCredentials: true });
      window.location.href = r.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start subscription.');
      setBusy(false);
    }
  };

  const feePct = Math.round((wallet?.platform_fee_rate || 0.1) * 100);

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 max-w-[1500px] mx-auto" data-testid="billing-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-black text-white">Billing</h1>
            <p className="text-xs text-gray-500">Wallet, subscription and invoice history — transparent {feePct}% platform fee on every Wave.</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-4 mb-6">
          <WalletBalanceCard />
          <SubscriptionCard wallet={wallet} plans={plans} subscribe={subscribe} busy={busy} />
        </div>

        {smartPresets.length > 0 && (
          <SmartPresetStrip presets={smartPresets} onSelect={(amt) => topup(amt)} busy={busy} feePct={feePct} />
        )}

        <div className="rounded-2xl bg-[#121212] border border-[#1f1f1f] p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold flex items-center gap-2"><Wallet className="h-4 w-4 text-[#00D4FF]" /> Top up wallet</h2>
              <p className="text-[11px] text-gray-500">Pick a preset or enter a custom amount. {feePct}% platform fee applies on Wave spend, shown before you send.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {(plans?.wallet_packages || []).map(pkg => (
              <button key={pkg.id} onClick={() => { setChosenPkg(pkg.id); setCustomAmount(''); }}
                      data-testid={`billing-topup-${pkg.id}`}
                      className={`py-4 rounded-xl border-2 text-center transition-all ${chosenPkg === pkg.id ? 'border-[#00D4FF] bg-[#00D4FF]/10' : 'border-[#1f1f1f] bg-[#0A0A0A] hover:border-[#00D4FF]'}`}>
                <p className="text-base font-black text-white">{pkg.label}</p>
                <p className="text-[9px] text-gray-500 mt-0.5">one-time</p>
              </button>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input type="number" placeholder={`Custom amount (USD, min $${plans?.custom_min || 500})`}
                   value={customAmount} onChange={e => { setCustomAmount(e.target.value); setChosenPkg(null); }}
                   data-testid="billing-custom-amount"
                   className="flex-1 px-4 py-3 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] focus:border-[#00D4FF] text-sm outline-none" />
            <button onClick={() => topup()} disabled={busy || (!chosenPkg && !customAmount)}
                    data-testid="billing-topup-btn"
                    className="px-5 py-3 rounded-lg bg-[#FF4D94] text-white font-bold text-xs disabled:opacity-50 hover:brightness-110 hover:scale-[1.02] transition-all flex items-center gap-2"
                    style={{ boxShadow: '0 0 16px rgba(255,77,148,0.3)' }}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CreditCard className="h-3.5 w-3.5" /> Checkout</>}
            </button>
          </div>
          {error && <p className="mt-3 text-[11px] text-red-400" data-testid="billing-error">{error}</p>}
        </div>

        <InvoiceTable txs={txs} feePct={feePct} />

        <div className="mt-6">
          <StripeConnectCard />
        </div>
      </div>
    </DashboardLayout>
  );
}

function SmartPresetStrip({ presets, onSelect, busy, feePct }) {
  return (
    <div className="mb-6 rounded-2xl p-5 relative overflow-hidden"
         style={{
           background: 'linear-gradient(135deg, rgba(0,212,255,0.08), rgba(255,77,148,0.06), #121212)',
           border: '1px solid rgba(0,212,255,0.25)',
         }}
         data-testid="smart-preset-strip">
      <div className="absolute -top-14 -right-14 w-48 h-48 rounded-full blur-3xl opacity-30" style={{ background: '#00D4FF' }} />
      <div className="relative flex items-center gap-2 mb-4">
        <Sparkles className="h-4 w-4 text-[#00D4FF]" />
        <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Smart Top-Up Presets</span>
        <span className="ml-auto text-[9px] text-gray-500">Based on your upcoming waves — {feePct}% fee included</span>
      </div>
      <div className="grid md:grid-cols-3 gap-3 relative">
        {presets.map(p => (
          <SmartPresetCard key={p.wave_id} preset={p} onSelect={onSelect} busy={busy} />
        ))}
      </div>
    </div>
  );
}

function SmartPresetCard({ preset, onSelect, busy }) {
  const covered = preset.covered;
  const buttonLabel = covered ? 'Pre-fund extra' : `Top up $${(preset.suggested_topup || preset.gross).toLocaleString()}`;
  const amount = covered ? preset.gross : (preset.suggested_topup || preset.gross);
  return (
    <div className="rounded-xl p-4 bg-[#0A0A0A] border border-[#1f1f1f] flex flex-col"
         data-testid={`smart-preset-${preset.wave_id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Waves className="h-3.5 w-3.5 text-[#FF4D94]" />
        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 truncate">{preset.wave_name}</span>
      </div>
      <p className="text-xl font-black text-white">${preset.gross.toLocaleString()}</p>
      <p className="text-[10px] text-gray-500 leading-snug mt-1">
        ${preset.budget.toLocaleString()} budget + <span className="text-[#FF4D94]">${preset.platform_fee.toLocaleString()} fee</span>
      </p>
      {covered ? (
        <p className="text-[10px] text-green-400 font-semibold mt-2 flex items-center gap-1"><Check className="h-3 w-3" /> Wallet already covers this wave</p>
      ) : (
        <p className="text-[10px] text-[#FF4D94] font-semibold mt-2">Short ${preset.shortfall.toLocaleString()} — top up below</p>
      )}
      <button onClick={() => onSelect(amount)} disabled={busy}
              data-testid={`smart-preset-btn-${preset.wave_id}`}
              className="mt-3 w-full py-2.5 rounded-lg bg-[#FF4D94] text-white font-bold text-xs hover:brightness-110 hover:scale-[1.02] transition-all disabled:opacity-60 flex items-center justify-center gap-1.5"
              style={{ boxShadow: '0 0 16px rgba(255,77,148,0.3)' }}>
        {buttonLabel} <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
}

function SubscriptionCard({ wallet, plans, subscribe, busy }) {
  const active = wallet?.subscription_status === 'active';
  const tier = plans?.subscriptions?.find(s => s.id === wallet?.subscription_tier);
  return (
    <div className="rounded-2xl p-5 bg-[#121212] border border-[#1f1f1f]" data-testid="subscription-card">
      <div className="flex items-center gap-2 mb-3">
        <Crown className="h-3.5 w-3.5 text-[#FF4D94]" />
        <span className="text-[9px] uppercase tracking-widest font-bold text-gray-400">Subscription</span>
      </div>
      {active && tier ? (
        <>
          <p className="text-2xl font-black text-white">{tier.name}</p>
          <p className="text-[11px] text-gray-400 mt-1">${tier.amount}/mo · {wallet.wave_credits_remaining} wave credit{wallet.wave_credits_remaining !== 1 ? 's' : ''} remaining</p>
          <div className="mt-4 rounded-lg bg-[#4ade8010] border border-[#4ade8030] px-3 py-2 flex items-center gap-2">
            <Check className="h-3.5 w-3.5 text-green-400" />
            <span className="text-[11px] text-green-400 font-semibold">Active — renews monthly</span>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-400 mb-3">Pick a plan to unlock included wave credits.</p>
          <div className="grid grid-cols-2 gap-2">
            {(plans?.subscriptions || []).map(t => (
              <button key={t.id} onClick={() => subscribe(t.id)} disabled={busy}
                      data-testid={`subscribe-${t.id}`}
                      className={`py-3 rounded-lg border-2 transition-all text-center ${t.id === 'pro' ? 'border-[#FF4D94] hover:bg-[#FF4D94]/10' : 'border-[#1f1f1f] hover:border-[#00D4FF]'}`}>
                <p className="text-xs font-bold text-white">{t.name}</p>
                <p className="text-[10px] text-gray-500">${t.amount}/mo</p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InvoiceTable({ txs, feePct }) {
  return (
    <div className="rounded-2xl bg-[#121212] border border-[#1f1f1f] p-5" data-testid="invoice-table">
      <h2 className="text-sm font-bold mb-4">Invoice & payment history</h2>
      {txs.length === 0 ? (
        <p className="text-xs text-gray-500 py-8 text-center">No transactions yet. Top up your wallet to see your first invoice here.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-500 border-b border-[#1f1f1f]">
                <th className="py-2.5 px-3 font-bold">Date</th>
                <th className="py-2.5 px-3 font-bold">Description</th>
                <th className="py-2.5 px-3 font-bold text-right">Amount</th>
                <th className="py-2.5 px-3 font-bold text-right">{feePct}% Fee</th>
                <th className="py-2.5 px-3 font-bold">Status</th>
                <th className="py-2.5 px-3 font-bold" />
              </tr>
            </thead>
            <tbody>
              {txs.map(tx => (
                <tr key={tx.session_id || `${tx.created_at}-${tx.description}`} className="border-b border-[#1a1a1a] hover:bg-[#0A0A0A]" data-testid={`invoice-row-${tx.session_id || tx.kind}`}>
                  <td className="py-3 px-3 text-gray-400">{formatDate(tx.created_at)}</td>
                  <td className="py-3 px-3 text-white font-semibold">{tx.description || tx.kind}</td>
                  <td className="py-3 px-3 text-right font-black text-[#00D4FF]">${Number(tx.amount || 0).toLocaleString()}</td>
                  <td className="py-3 px-3 text-right text-gray-400">{tx.platform_fee ? `$${Number(tx.platform_fee).toLocaleString()}` : '—'}</td>
                  <td className="py-3 px-3"><StatusPill status={tx.payment_status} /></td>
                  <td className="py-3 px-3 text-right">
                    <button onClick={() => downloadPdf(tx)} data-testid={`download-pdf-${tx.session_id || tx.kind}`}
                            className="text-[10px] text-[#00D4FF] hover:underline flex items-center gap-1 ml-auto">
                      <Download className="h-3 w-3" /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: { bg: '#4ade8015', fg: '#4ade80', label: 'Paid' },
    initiated: { bg: '#fbbf2415', fg: '#fbbf24', label: 'Pending' },
    failed: { bg: '#ef444415', fg: '#ef4444', label: 'Failed' },
    expired: { bg: '#71717a15', fg: '#71717a', label: 'Expired' },
  };
  const m = map[status] || { bg: '#71717a15', fg: '#71717a', label: status || 'Unknown' };
  return <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: m.bg, color: m.fg }}>{m.label}</span>;
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return String(d); }
}

function downloadPdf(tx) {
  // Printable HTML receipt — built via Blob URL (not document.write) to avoid
  // the XSS/clobbering risks of writing into a cross-origin window.
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${escapeHtml(tx.session_id || '')}</title>
    <style>body{font-family:Inter,system-ui,sans-serif;padding:40px;max-width:720px;margin:auto}
    h1{color:#0A0A0A}table{width:100%;border-collapse:collapse;margin-top:20px}
    td{padding:10px 0;border-bottom:1px solid #eee}.muted{color:#888;font-size:12px}</style></head>
    <body>
      <h1>WAVE Invoice</h1>
      <p class="muted">Session: ${escapeHtml(tx.session_id || '—')}</p>
      <table>
        <tr><td><strong>Date</strong></td><td>${escapeHtml(formatDate(tx.created_at))}</td></tr>
        <tr><td><strong>Description</strong></td><td>${escapeHtml(tx.description || tx.kind || '')}</td></tr>
        <tr><td><strong>Amount</strong></td><td>$${Number(tx.amount || 0).toLocaleString()}</td></tr>
        ${tx.platform_fee ? `<tr><td><strong>Platform fee (10%)</strong></td><td>$${Number(tx.platform_fee).toLocaleString()}</td></tr>` : ''}
        <tr><td><strong>Status</strong></td><td>${escapeHtml(tx.payment_status || '')}</td></tr>
      </table>
      <p class="muted" style="margin-top:40px">Thank you for riding with WAVE.</p>
      <button onclick="window.print()">Print / Save as PDF</button>
    </body></html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) URL.revokeObjectURL(url); // popup blocked — drop the blob
  // The blob URL is revoked by the browser when the tab closes.
}

function escapeHtml(s) {
  return String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
